import { ParameterizedRouteDefinition, StaticRouteDefinition, route } from '@betterment/js-runtime';

import brochureUrl from './brochureUrl';
import { preserveFirmKey } from './path-helpers';

import { AppContextType } from '../apps/MultidocUploadFlowApp';
import { Account, Card, Invitation } from '../Checking/data';

/**
 * Utility that wraps route() to build navigation routes that preserve `/:firmKey?` and `/app`
 * when called with .absolute()
 *
 * the components passed to this utility should always start after /app
 *
 * @param components - An array of strings or objects with a `param` property. the same as route(_, components)
 */
type Param<P> = { param: P };
type Component<P> = string | Param<P>;
function routeBuilder(components: string[]): StaticRouteDefinition;
function routeBuilder<P extends string>(components: Component<P>[]): ParameterizedRouteDefinition<P>;
function routeBuilder<P extends string>(components: string[] | Component<P>[]) {
  return route(preserveFirmKey('/app'), components);
}

export const urlBuilder = {
  // fetch urls (eg: queries, mutations, file downloads)
  apiCheckingAccountDirectDepositPdf: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'direct_deposit_form',
  ]),
  apiGraphQL: route('', ['api/graphql/web']),
  apiV2AddGoalAllocationSelection: route('/api/v2', ['add_goal_flows', { param: 'flowId' }, 'allocation_selection']),
  apiV2AddGoalAvailableAllocations: route('/api/v2', ['add_goal_flows', { param: 'flowId' }, 'available_allocations']),
  apiV2AdviceInitiationCalendlyCall: route('/api/v2', ['advice_initiation', 'calendly_call']),
  apiV2AdviceInitiationIntendedTransferAmount: route('/api/v2', ['advice_initiation', 'intended_transfer_amount']),
  apiV2AdviceInitiationPremiumUpgrade: route('/api/v2', ['advice_initiation', 'premium_upgrade']),
  apiV2Annual401kDeferralProjection: route('/api/v2', ['annual_401k_deferral_projection']),
  apiV2AutomaticDepositConfig: route('/api/v2', ['automatic_deposit_configs', { param: 'automaticDepositConfigId' }]),
  apiV2AutomaticWithdrawalConfig: route('/api/v2', [
    'automatic_withdrawal_configs',
    { param: 'automaticWithdrawalConfigId' },
  ]),
  apiV2BankAccountCreationFlow: route('/api/v2', ['bank_account_creation_flows']),
  apiV2CheckingAccountCreationRequests: route('/api/v2', ['checking', 'account_creation_requests']),
  apiV2CheckingAccountCreationRequestsCompletion: route('/api/v2', [
    'checking',
    'account_creation_requests',
    { param: 'requestId' },
    'completion',
  ]),
  apiV2CheckingBankLinkException: route('/api/v2', ['checking', 'bank_link_exceptions']),
  apiV2CheckingDetailedTransaction: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'detailed_transactions',
    { param: 'transactionId' },
  ]),
  apiV2CheckingDetailedTransactions: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'detailed_transactions',
  ]),
  apiV2CheckingDirectDepositConfig: route('/api/v2', [
    'checking',
    'accounts',
    { param: 'accountId' },
    'direct_deposit_config',
  ]),
  apiV2CheckingExternalAccounts: route('/api/v2', ['checking', 'external_accounts']),
  apiV2CheckingJointInvitationRequest: route('/api/v2', ['checking', 'joint_invitation_requests']),
  apiV2CheckingJointInvitationRequestCompletion: route('/api/v2', [
    'checking',
    'joint_invitation_requests',
    { param: 'requestId' },
    'completion',
  ]),
  apiV2CheckingLinkableFundingAccounts: route('/api/v2', ['checking', 'linkable_funding_accounts']),
  apiV2CheckingStateAgeLimitEligibility: route('/api/v2', ['checking', 'state_age_limit_eligibility']),
  apiV2DepositBankAccountsList: route('/api/v2', ['deposit_bank_accounts']),
  apiV2DepositDestinationAccounts: route('/api/v2', ['deposit_destination_accounts']),
  apiV2DepositNextStepPath: route('/api/v2', ['deposit_next_step_path']),
  apiV2DocVerification: route('/api/v2', ['document_verification']),
  apiV2EditGoalProfile: route('/api/v2', ['goals', { param: 'goalId' }, 'edit_profile']),
  apiV2EducationSavingsFiveTwoNineAscensusAccounts: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_accounts',
  ]),
  apiV2EducationSavingsFiveTwoNineAscensusEnrollments: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_enrollments',
  ]),
  apiV2EducationSavingsFiveTwoNineAscensusPlanConnections: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_plan_connections',
  ]),
  apiV2EducationSavingsFiveTwoNineAscensusSsoConnectionIntents: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'ascensus_sso_connection_intents',
  ]),
  apiV2EducationSavingsFiveTwoNineEnrollmentDetailsValidation: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'enrollment_details_validations',
  ]),
  apiV2EducationSavingsFiveTwoNineExternalAccountConnectionIntents: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'external_account_connection_intents',
  ]),
  apiV2EducationSavingsFiveTwoNinePlanIntents: route('/api/v2', ['education_savings', 'five_two_nine', 'plan_intents']),
  apiV2EducationSavingsFiveTwoNinePlanSelectionAdvice: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'plan_selection_advice',
  ]),
  apiV2EducationSavingsFiveTwoNinePortfolioIntents: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'portfolio_intents',
  ]),
  apiV2EducationSavingsFiveTwoNinePortfolioSelectionAdvice: route('/api/v2', [
    'education_savings',
    'five_two_nine',
    'portfolio_selection_advice',
  ]),
  apiV2EmailSubmission: route('/api/v2', ['email_submission']),
  apiV2EmploymentEmployeeEstimatedSlmPayment: route('/api/v2', ['employment', 'employee_estimated_slm_payments']),
  apiV2ExternallyAdvisedPlanAllocations: route('/api/v2', [
    'goals',
    { param: 'goalId' },
    'edit_portfolio_flows',
    'externally_advised_plan_allocations',
  ]),
  apiV2GlobalNotices: route('', ['api/v2', 'global_notices']),
  apiV2Goal: route('/api/v2', ['goals', { param: 'goalId' }]),
  apiV2GoalsProjectedBalance: route('/api/v2', ['goals', { param: 'goalId' }, 'projected_balance']),
  apiV2GoalsRetirementProjections: route('/api/v2', ['goals', { param: 'goalId' }, 'retirement_projections']),
  apiV2InAppContentBlogPosts: route('/api/v2', ['in_app_content', 'blog_posts']),
  apiV2IrsStateWithholdingRules: route('/api/v2', ['ira_state_withholding_rules']),
  apiV2IrsStateWithholdingValidation: route('/api/v2', ['ira_state_withholding_validation']),
  apiV2LegalAccountsCheckingBankAccounts: route('/api/v2', [
    'legal_accounts',
    { param: 'legalAccountId' },
    'checking_bank_accounts',
  ]),
  apiV2LegalAccountsExternalAccountBankAccounts: route('/api/v2', [
    'legal_accounts',
    { param: 'legalAccountId' },
    'external_account_bank_accounts',
  ]),
  apiV2LegalAccountsLinkableFundingAccounts: route('/api/v2', [
    'legal_accounts',
    { param: 'legalAccountId' },
    'linkable_funding_accounts',
  ]),
  apiV2ManualBankAccountVerifications: route('/api/v2', ['manual_bank_account_verifications']),
  apiV2MarketingMessages: route('', ['api/v2', 'marketing_messages']),
  apiV2MarketingMessagesGroups: route('', ['api/v2', 'marketing_messages_groups']),
  apiV2MessageDismissals: route('', ['api/v2', 'message_dismissals', { param: 'name' }]),
  apiV2OnboardingAccounts: route('/api/v2', ['onboarding', 'accounts']),
  apiV2OnboardingDraftAccounts: route('/api/v2', ['onboarding', 'draft_accounts']),
  apiV2OnboardingProfileSetup: route('/api/v2', ['onboarding', 'profile_setups']),
  apiV2OnboardingUsers: route('/api/v2', ['onboarding', 'users']),
  apiV2PayrollDeductionChanges: route('/api/v2', ['payroll_deduction_changes']),
  apiV2PlaidItem: route('/api/v2', ['plaid', 'items', { param: 'plaidItemId' }]),
  apiV2PlaidItemExternalAccounts: route('/api/v2', ['plaid', 'items', { param: 'plaidItemId' }, 'external_accounts']),
  apiV2PlaidItemRefreshes: route('/api/v2', ['plaid', 'items', { param: 'plaidItemId' }, 'refreshes']),
  apiV2PlaidItems: route('/api/v2', ['plaid', 'items']),
  apiV2PlaidLinkTokens: route('/api/v2', ['plaid', 'link_tokens']),
  apiV2PlaidUpdateModeLinkTokens: route('/api/v2', ['plaid', 'update_mode_link_tokens']),
  apiV2ProfileSetupFlowCompletions: route('/api/v2', ['users', 'profile_setup_flow_completion']),
  apiV2RecurringTransferConfig: route('/api/v2', [
    'recurring_transfer_configs',
    { param: 'recurringTransferConfigId' },
  ]),
  apiV2RolloverReminderDismissals: route('/api/v2', ['rollovers', { param: 'rolloverId' }, 'reminder_dismissals']),
  apiV2RothLimit: route('/api/v2', ['roth_limits', { param: 'taxYear' }]),
  apiV2SubAccountCashMatchFeeSearch: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'cash_match_fee_search',
  ]),
  apiV2SubAccountExcessContributionCalculations: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'excess_contribution_calculations',
  ]),
  apiV2SubAccountIraContributionLimits: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'ira_contribution_limits',
  ]),
  apiV2SubAccountWireTransfers: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'wire_transfers']),
  apiV2SubAccountWireTransfersNew: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'wire_transfers',
    'new',
  ]),
  apiV2SubAccountsDeposits: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'deposits']),
  apiV2SubAccountsIraContributions: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'ira_contributions']),
  apiV2SubAccountsWithdrawalCalculations: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'withdrawal_calculations',
  ]),
  apiV2SubAccountsWithdrawals: route('/api/v2', ['sub_accounts', { param: 'subAccountId' }, 'withdrawals']),
  apiV2SubaccountsWithdrawalTaxImpacts: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'withdrawal_tax_impacts',
  ]),
  apiV2TransferSourceAccounts: route('/api/v2', ['transfer_source_accounts']),
  apiV2TransferredUserOnboardingsAddressValidation: route('/api/v2', [
    'transferred_user_onboardings',
    { param: 'invitationToken' },
    'address_validation',
  ]),
  apiV2TransferredUserOnboardingsCompletion: route('/api/v2', [
    'transferred_user_onboardings',
    { param: 'invitationToken' },
    'completion',
  ]),
  apiV2TransferredUserOnboardingsIdentityVerification: route('/api/v2', [
    'transferred_user_onboardings',
    { param: 'invitationToken' },
    'identity_verification',
  ]),
  apiV2User: route('/api/v2', ['users']),
  apiV2ValidateAddress: route('/api/v2', ['address_validation']),
  apiV2ValidateGoalName: route('/api/v2', ['goals', { param: 'goalId' }, 'name_validation']),
  apiV2ValidatePhoneNumber: route('/api/v2', ['phone_number_validation']),
  createCheckingAccountTransfer() {
    return `/api/v2/checking/transfers`;
  },
  createDeclineInvitationRequest(invitationId: Invitation['invitationId']) {
    return `/api/v2/checking/invitations/${invitationId}/declination`;
  },
  createDepositBankAccountsList() {
    return '/api/v2/deposit_bank_accounts';
  },
  createDetailedTransactionCancellation(transactionId: number | string) {
    return `/api/v2/checking/detailed_transactions/${transactionId}/cancellation`;
  },
  createDetailedTransactionExport(accountId: number) {
    return `/api/v2/checking/accounts/${accountId}/detailed_transaction_exports`;
  },
  createEditAccessRequest(invitationId: Invitation['invitationId']) {
    return `/api/v2/checking/invitations/${invitationId}/acceptance`;
  },
  createNextStepPaths() {
    return `/api/v2/transfer_next_step_path`;
  },
  createProspectSecondaryJointInvitationRequest() {
    return '/api/v2/checking/prospect_secondary_joint_invitation_requests';
  },
  createSubAccountTransfer(subAccountId: number) {
    return `/api/v2/sub_accounts/${subAccountId}/transfers`;
  },
  createSubAccountTransferTaxImpact(subAccountId: number) {
    return `/api/v2/sub_accounts/${subAccountId}/transfer_tax_impacts`;
  },
  createTransferDestinationAccountsList() {
    return '/api/v2/transfer_destination_accounts';
  },
  createWithdrawalBankAccountsList() {
    return '/api/v2/withdrawal_bank_accounts';
  },
  createWithdrawalNextStepPaths() {
    return `/api/v2/withdrawal_next_step_path`;
  },
  getBankDocuments() {
    return '/api/v2/checking/bank_documents';
  },
  getCheckingAccountTasks: route('/api/v2', ['checking', 'accounts', { param: 'accountId' }, 'tasks']),
  getCheckingSignupData: route('/api/v2', ['checking', 'signup_data']),
  getCustomer() {
    return '/api/v2/checking/customer';
  },
  getDepositAvailabilityEstimate() {
    return '/api/v2/checking/deposit_availability_date_estimate';
  },
  getDepositDestinationAccounts() {
    return '/api/v2/deposit_destination_accounts';
  },
  getDetailedTransactionExport(accountId: Account['id'], exportId: string) {
    return `/api/v2/checking/accounts/${accountId}/detailed_transaction_exports/${exportId}`;
  },
  getGoalImage(goalId: number) {
    return `/api/v2/goals/${goalId}/image.png`;
  },
  getIraRecharacterizationEarningsAmountCents() {
    return '/api/v2/ira_recharacterization/earnings';
  },
  getSubAccountWithdrawalDistributionTypes: route('/api/v2', [
    'sub_accounts',
    { param: 'subAccountId' },
    'withdrawal_distribution_types',
  ]),
  getSudoCheck() {
    return '/api/v2/sudo_check';
  },
  getSummaryTransitionaryGoals() {
    return '/api/v2/summary/transitionary_goals';
  },
  getUploadMultidocData(appContext: AppContextType) {
    if (appContext === 'investing') return '/api/v2/multidoc_uploads';
    return '/api/v2/checking_multidoc_uploads';
  },
  getUser() {
    return '/api/v2/current_user';
  },
  getWithdrawalAvailabilityEstimate() {
    return '/api/v2/checking/withdrawal_availability_date_estimate';
  },
  getWithdrawalRecommendation() {
    return '/api/v2/withdrawal_recommendations';
  },
  getWithdrawalSourceAccounts() {
    return '/api/v2/withdrawal_source_accounts';
  },
  markFirmFormCRSDelivered: route('/api/v2', ['firm_form_crs_notification_deliveries']),
  patchPersonalReferralInvites(personalReferralInviteId: string) {
    return `/api/v2/refer_a_friend/personal_referral_invites/${personalReferralInviteId}`;
  },
  postActivateCard(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/verification`;
  },
  postAdvisorInvitesAcceptances(inviteId: string) {
    return `/api/v2/advisor_invites/${inviteId}/acceptances`;
  },
  postCancelJointCheckingInvitation(jointAccountInvitationId: Invitation['invitationId']) {
    return `/api/v2/checking/invitations/${jointAccountInvitationId}/cancellation`;
  },
  postCardLock(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/lock`;
  },
  postCardUnlock(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/unlock`;
  },
  postCheckingCustomerCreation: route('/api/v2', ['checking', 'customer_creation']),
  postDeposit(accountId: Account['id']) {
    return `/api/v2/checking/accounts/${accountId}/deposits`;
  },
  postInitialPinSetup(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/initial_pin_setup`;
  },
  postIraRecharacterizationFlowSubmission() {
    return '/api/v2/ira_recharacterization/creation';
  },
  postPersonalReferralInvites() {
    return '/api/v2/refer_a_friend/personal_referral_invites';
  },
  postPinReset(cardId: number) {
    return `/api/v2/checking/cards/${cardId}/pin_reset`;
  },
  postSwitchSpendingAccount(cardId: Card['id']) {
    return `/api/v2/checking/cards/${cardId}/spending_account`;
  },
  postValidateAddress() {
    return '/api/v2/address_validation';
  },
  postWithdrawal(accountId: Account['id']) {
    return `/api/v2/checking/accounts/${accountId}/withdrawals`;
  },
  showReceivedInvitation(invitationId: Invitation['invitationId']) {
    return `/api/v2/checking/received_invitations/${invitationId}`;
  },
  postSkipTask() {
    return '/app/user_task_skip';
  },
  updatePreferredName: route('', ['app', 'preferred_name_change_flow']),

  // FIXME: refactor to just static strings
  // external links
  getAndroidGooglePlayStoreUrl() {
    return 'https://play.google.com/store/apps/details?id=com.betterment&hl=en_US&gl=US';
  },
  getEBSALookAt401kPlanFeesUrl() {
    return 'https://www.dol.gov/sites/dolgov/files/EBSA/about-ebsa/our-activities/resource-center/publications/a-look-at-401k-plan-fees.pdf';
  },
  getGoogleFinanceUrl() {
    return 'https://www.google.com/finance';
  },
  getIosAppStoreUrl() {
    return 'https://apps.apple.com/us/app/betterment-investing-saving/id393156562';
  },
  getSendSafelyLegalNameChangeUrl() {
    return 'https://sendsafely.betterment.com/dropzone/legal_name_changes';
  },

  // FIXME: use route() instead where applicable and refactor to a static string where not
  // brocher urls
  getAdv() {
    return brochureUrl('/adv');
  },
  getAssetLocationMethodologyPage() {
    return brochureUrl('/resources/asset-location-methodology');
  },
  getBrochureUrl() {
    return brochureUrl('/');
  },
  getCashReserveTermsAndConditions() {
    return brochureUrl('/legal/cash-reserve');
  },
  getContactPage() {
    return brochureUrl('/contact');
  },
  getCryptoAccountBalanceUrl() {
    return brochureUrl('/legal/portfolio-minimum');
  },
  getCustomerAgreement() {
    return brochureUrl('/customeragreement');
  },
  getDisqualifyingEventsPage() {
    return brochureUrl('/legal/marketing-rule-disqualifying-events');
  },
  getEmployeesResourceCenter(article: string) {
    return brochureUrl(`/employees/resources/${article}`);
  },
  getGoalMAGIIncomeCalculationHelp() {
    return brochureUrl('/help/retirement-goal-magi-income-calculation');
  },
  getHelpCenter(article?: string) {
    return article ? brochureUrl(`/help/${article}`) : brochureUrl(`/help`);
  },
  getIraSpecialConsiderations() {
    return brochureUrl('/help/inherited-ira-special-considerations');
  },
  getLegalDisclosure(disclosure: string) {
    return brochureUrl(`/legal/${disclosure}`);
  },
  getLegalDisclosures() {
    return brochureUrl('/legal');
  },
  getLegalFormAdv() {
    return brochureUrl('/legal/form-adv-part-2');
  },
  getLegalFormCrs() {
    return brochureUrl('/legal/form-crs');
  },
  getLimitedScopeAgreement() {
    return brochureUrl('/legal/limitedscopeagreement');
  },
  getPricingUrl() {
    return brochureUrl('/pricing');
  },
  getPrivacyPolicy() {
    return brochureUrl('/legal/privacy-policy');
  },
  getTaxWithholdingNotice() {
    return brochureUrl('/legal/tax-withholding-notice');
  },
  getTcpDisclosure() {
    return brochureUrl('/legal/tax-coordination');
  },
  getTradAndRoth401kEmployeeResourceUrl() {
    return brochureUrl('/employees/resources/traditional-and-roth-401ks');
  },
  getSelfDirectedInvestingDisclosure() {
    return brochureUrl('/legal/self-directed-investing-disclosure');
  },
  getTransferSweepProgramDisclosure() {
    return brochureUrl('/legal/tsp');
  },

  // unauthenticated routes without firmKey
  appBlogPosts: route('/app', ['blog_posts']),
  appSignup: route('/app', ['signup']),

  // routes for navigation
  addGoal: routeBuilder(['add_goal']),
  appAddGoalFlowsNew: routeBuilder(['add_goal_flows', 'new']),
  appAdviceAccountContributionsEdit: routeBuilder([
    'goals',
    { param: 'goalId' },
    'advice_accounts_contributions',
    'edit',
  ]),
  appAdviceInitiationFlow: routeBuilder(['advice_initiation']),
  appAscensusSsoInitiations: routeBuilder(['ascensus', 'sso_initiation']),
  appAutomaticDepositConfig: routeBuilder(['automatic_deposit_configs', { param: 'automaticDepositConfigId' }]),
  appAutomaticDepositConfigEdit: routeBuilder([
    'automatic_deposit_configs',
    { param: 'automaticDepositConfigId' },
    'edit',
  ]),
  appAutomaticWithdrawalConfig: routeBuilder([
    'automatic_withdrawal_configs',
    { param: 'automaticWithdrawalConfigId' },
  ]),
  appAutomaticWithdrawalConfigEdit: routeBuilder([
    'automatic_withdrawal_configs',
    { param: 'automaticWithdrawalConfigId' },
    'edit',
  ]),
  appBankAccountSetup: routeBuilder(['legal_accounts', { param: 'legalAccountId' }, 'bank_account_setup']),
  appBlogPost: routeBuilder(['blog_posts', { param: 'postId' }]),
  appCharitableGiftStart: routeBuilder(['charitable_gift_start']),
  appConvertFundsToRoth: routeBuilder(['sub_accounts', { param: 'subAccountId' }, 'ira_conversion_flows', 'new']),
  appCryptoOverview: routeBuilder(['crypto_overview']),
  appDeferralSetting: routeBuilder(['legal_accounts', { param: 'legalAccountId' }, 'deferral_setting', 'edit']),
  appDepositRequestMemo: routeBuilder(['deposit_requests', { param: 'depositRequestId' }, 'memo', 'new']),
  appDepositSetup: routeBuilder(['deposit_setup']),
  appDepositSetupDestination: routeBuilder(['deposit_setup', 'destination']),
  appDistributionRequestMemo: routeBuilder(['distributions', { param: 'distributionRequestId' }, 'memo', 'new']),
  appEarnedRewards: routeBuilder(['earned_rewards']),
  appEndOfYearAttestationApp: routeBuilder(['employment', 'attested_student_loan_payments']),
  appEstimatedStudentLoanPaymentFlow: routeBuilder(['employment', 'estimated_student_loan_payments']),
  appExcessContributionRemovalRequestMemo: routeBuilder([
    'excess_contribution_removals',
    { param: 'excessContributionRemovalRequestId' },
    'memo',
    'new',
  ]),
  appExternalAccountAssociation: routeBuilder(['goals', { param: 'goalId' }, 'external_account_association', 'new']),
  appExternalAccountSetup: routeBuilder(['external_account_setup']),
  appExternalAccounts: routeBuilder(['external_accounts']),
  appExternalAccountsHoldings: routeBuilder(['external_accounts', { param: 'externalAccountId' }, 'holdings']),
  appExternalAccountsNew: routeBuilder(['external_accounts', 'new']),
  appExternalAccountsRolloverStart: routeBuilder([
    'external_accounts',
    { param: 'externalAccountId' },
    'rollover_start',
  ]),
  appExternalTaxableAccountTransferFlowsNew: routeBuilder(['external_taxable_account_transfer_flows', 'new']),
  appFinancialProfileEdit: routeBuilder(['financial_profile', 'edit']),
  appFiveTwoNineAccountDetails: routeBuilder(['five_two_nine_account_details', { param: 'planIntentId' }]),
  appFiveTwoNineCompleteGoalSetup: routeBuilder(['goals', { param: 'goalId' }, 'five_two_nine', 'complete_goal_setup']),
  appFiveTwoNineConnectUnite: routeBuilder(['five_two_nine_connect_unite']),
  appFiveTwoNineOhioCDPurchaseFlow: routeBuilder([
    'goals',
    { param: 'goalId' },
    'five_two_nine',
    'ohio_cd_purchases_completion',
    'flow',
  ]),
  appFiveTwoNineOhioCDPurchaseSuccess: routeBuilder([
    'goals',
    { param: 'goalId' },
    'five_two_nine',
    'ohio_cd_purchases_completion',
    'success',
  ]),
  appFiveTwoNinePortfolioSelection: routeBuilder(['five_two_nine_portfolio_selection', { param: 'planIntentId' }]),
  appFiveTwoNineSignup: routeBuilder(['five_two_nine_signup']),
  appFixConnection: routeBuilder(['plaid_items', { param: 'plaidItemId' }, 'fix_connection']),
  appGoalCreationIntents: routeBuilder(['goal_creation_intents', { param: 'goalCreationIntentId' }]),
  appGoalOverview: routeBuilder(['goals', { param: 'goalId' }, 'overview']),
  appGoalRecommendation: routeBuilder(['goal_recommendation']),
  appGoalsEditAllocation: routeBuilder(['goal_allocation_change_flows', 'new']),
  appGoalsEditPortfolioStrategy: routeBuilder([
    'goals',
    { param: 'goalId' },
    'edit_portfolio',
    'portfolio_strategy_selection',
    'new',
  ]),
  appGoalsEditProfileName: routeBuilder(['goals', { param: 'goalId' }, 'edit_profile', 'goal_name']),
  appGoalsEditPurpose: routeBuilder(['edit_goal_details_flows', 'new']),
  appGoalsEditPersonalizedRetirementPlan: routeBuilder([
    'goals',
    { param: 'goalId' },
    'personalized_retirement_goal_plan_update',
    'new',
  ]),
  appGoalTaxLocationChangeNew: routeBuilder(['goals', { param: 'goalId' }, 'tax_location_changes', 'new']),
  appImpersonationBlocker: routeBuilder(['impersonation_blocker']),
  appLogin: routeBuilder(['login']),
  appOnboardingFlow: routeBuilder(['onboarding_flow']),
  appPayrollAssumptions: routeBuilder(['employees', { param: 'employeeId' }, 'payroll_assumptions', 'new']),
  appPayrollDeductionSetup: routeBuilder(['payroll_deduction_setup']),
  appPerformance: routeBuilder(['performance']),
  appPlaidLink: routeBuilder(['plaid_link']),
  appProfile: routeBuilder(['settings']),
  appProfileSetupFlow: routeBuilder(['profile_setup_flow']),
  appTransfersNewTransfer: routeBuilder(['transfers', 'new_transfer']),
  appRecurringTransferConfig: routeBuilder(['recurring_transfer_configs', { param: 'recurringTransferConfigId' }]),
  appRecurringTransferConfigEdit: routeBuilder([
    'recurring_transfer_configs',
    { param: 'recurringTransferConfigId' },
    'edit',
  ]),
  appRewards: routeBuilder(['rewards']),
  appRolloverDocument: routeBuilder(['rollovers', { param: 'rolloverId' }, 'document']),
  appSpousalRelationshipFlow: routeBuilder(['spousal_relationship_flow']),
  appSubAccountOneTimeDepositSetupNew: routeBuilder([
    'sub_accounts',
    { param: 'subAccountId' },
    'one_time_deposits',
    'new',
  ]),
  appTlhSetupSuitabilityIntroduction: routeBuilder(['tlh_setup', 'tlh_suitability', 'introduction']),
  appTransferredUserOnboarding: routeBuilder(['transferred_user_onboardings', { param: 'invitationToken' }]),
  appWithdrawalRequestMemo: routeBuilder(['withdrawals', { param: 'withdrawalRequestId' }, 'memo', 'new']),
  appWithdrawalSetup: routeBuilder(['withdrawal_setup']),
  getAppDocumentVerifications: routeBuilder(['document_verifications']),
  getCheckingMultidocUploadPage: routeBuilder(['checking_multidoc_uploads']),
  getCheckingMultidocUploadsSuccess: routeBuilder(['multidoc_uploads', 'success']),
  getDocuments: routeBuilder(['documents']),
  getGoalSettings: routeBuilder(['goals', { param: 'goalId' }, 'settings']),
  getInvestingMultidocUploadPage: routeBuilder(['multidoc_uploads']),
  getIraRecharacterization: routeBuilder(['ira_recharacterizations']),
  getLegalAccounts: routeBuilder(['legal_accounts']),
  getLegalNameChangeFlowPage: routeBuilder(['legal_name_change_flow']),
  getOfferTerms: routeBuilder(['offers', { param: 'offerName' }]),
  getOtherDocuments: routeBuilder(['other_documents']),
  getPendingTransfers: routeBuilder(['transfers', 'pending_transfers']),
  getPreferredNameChangePage: routeBuilder(['preferred_name_change_flow', 'edit']),
  getRollovers: routeBuilder(['rollovers', 'new']),
  getTaxLocationSetup: routeBuilder(['tax_location_setup', 'introduction']),
  new401kDistributionTypeSelection: routeBuilder([
    'sub_accounts',
    { param: 'subAccountId' },
    'four_oh_one_k_distribution_type_selections',
    'new',
  ]),
  newAppFourOhOneKInternalRolloverConfirmation: routeBuilder(['four_oh_one_k_internal_rollover_confirmation', 'new']),
  newManualBankAccount: routeBuilder(['legal_accounts', { param: 'legalAccountId' }, 'manual_bank_accounts', 'new']),
  showEditGoalProfileFlow: routeBuilder(['goals', { param: 'goalId' }, 'edit_profile']),
  showHowToImproveGoal: routeBuilder(['goals', { param: 'goalId' }, 'pricing', 'how_to_improve']),
  showSubAccountTransferSetup: routeBuilder(['sub_accounts', { param: 'subAccountId' }, 'transfer_setup']), // query params: goal_id, skip_to_step, return_to
  showTransferSetup: routeBuilder(['transfer_setup']),
  appB4bDocuments: routeBuilder(['b4b', 'documents']),
  appTrustedContactNew: routeBuilder(['trusted_contact', 'new']),
  // Pangea routes
  appAddAccount: routeBuilder(['add_account']),
  appAccount: routeBuilder(['account', { param: 'accountId' }]),
  appAccountBuyOrder: routeBuilder(['account', { param: 'accountId' }, 'buy_order', { param: 'securityId' }]),
  appAccountForecaster: routeBuilder(['account', { param: 'accountId' }, 'forecaster']),
  appAccountSecurity: routeBuilder(['account', { param: 'accountId' }, 'security', { param: 'securityId' }]),
  appAccountSellOrder: routeBuilder(['account', { param: 'accountId' }, 'sell_order', { param: 'securityId' }]),
  appAccountPerformance: routeBuilder(['account', { param: 'accountId' }, 'performance']),
  appActivity: routeBuilder(['activity']),
  appActivityTransactions: routeBuilder(['activity_transactions']),
  appEditUserLevelSettings: routeBuilder(['edit_user_level_settings']),
  appExternallyAdvisedPlan: routeBuilder(['goals', { param: 'goalId' }, 'edit_portfolio', 'externally_advised_plan']),
  appFiveTwoNineOhioCDPurchase: routeBuilder([
    'goals',
    { param: 'goalId' },
    'five_two_nine',
    'ohio_cd_purchases_completion',
  ]),
  appFourOhOneKDetails: routeBuilder(['four_oh_one_k_details']),
  appFourOhOneKSelect: routeBuilder(['four_oh_one_k_select']),
  appHome: routeBuilder([]),
  appIraDetails: routeBuilder(['ira_details']),
  appPersonalInfoEdit: routeBuilder(['personal_info', 'edit']),
  appPersonalReferralInvites: routeBuilder(['personal_referral_invites']),
  appPurpose: routeBuilder(['purpose', { param: 'purposeId' }]),
  appPurposeForecaster: routeBuilder(['purpose', { param: 'purposeId' }, 'forecaster']),
  emailVerificationCheckpoint: routeBuilder(['email_verification_checkpoint']),
  // Checking routes
  appCheckingExternalAccountSetup: routeBuilder(['checking', 'external_account_setup']),
  appCheckingJointAccountsInvitation: routeBuilder([
    'checking',
    'joint_accounts_invitations',
    { param: 'jointAccountInvitationId' },
  ]),
  appCheckingSignups: routeBuilder(['checking', 'signups']),
  appDeferralSettings: routeBuilder(['employers', { param: 'employerId' }]),
  getAppChecking: routeBuilder(['checking']),
  getAppCheckingAbout: routeBuilder(['checking', 'about']),
  getAppCheckingAccountCreation: routeBuilder(['checking', 'account_creation']),
  getAppCheckingAccountDepositSetup: routeBuilder(['checking', 'accounts', { param: 'accountId' }, 'deposit_setup']),
  getAppCheckingAccountDirectDeposit: routeBuilder(['checking', 'accounts', { param: 'accountId' }, 'direct_deposit']),
  getAppCheckingAccountTransactions: routeBuilder(['checking', 'accounts', { param: 'accountId' }, 'transactions']),
  getAppCheckingCardSetup: routeBuilder(['checking', 'card_setup']),
  getAppCheckingJointAccounts: routeBuilder(['checking', 'joint_accounts']),
  getAppCheckingJointInvitationAcceptanceSuccess: routeBuilder([
    'checking',
    'joint_accounts_invitations',
    { param: 'jointAccountInvitationId' },
    'success',
  ]),
  getAppCheckingPinChange: routeBuilder(['checking', 'pin_change']),
  getAppCheckingProspectSignup: routeBuilder(['checking', 'prospect_signup']),
  getAppCheckingSummary: routeBuilder(['checking', 'accounts', { param: 'accountId' }]),
  getCheckingFundingOptions: routeBuilder(['checking', 'accounts', { param: 'accountId' }, 'funding_options']),
  getCheckingWithdrawalSetup: routeBuilder(['checking', 'accounts', { param: 'accountId' }, 'withdrawal_setup']),
};
